import Table from '@shared/components/Table';
import useFetchData from '@shared/hooks/useFetchData';
import { useState, useEffect } from 'react';
import { noticePeriodService } from '../services/noticePeriod';
import { useTranslation } from 'react-i18next';
import { useStatusBadge } from '@shared/components/StatusBadge';
import ActionButtons from '@shared/components/ActionButtons';
import { COMMON_BUTTONS } from '@config/actionButtonConfigs';
import NoticePeriodForm from './NoticePeriodForm';
import useModal from "@shared/hooks/useModal";
import { useForm } from 'react-hook-form';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import DeleteModal from '@shared/components/DeleteModal';
import { useDeleteHandler } from '@shared/hooks/useDeleteHandler';
import NoticePeriodTranslation from './NoticePeriodTranslation';

export default function NoticePeriod() {

    const { t } = useTranslation();
    const { pageNumber, fetchData, pageSize, sortBy, sortDir, data, totalPages, totalElements, setpageNumber, setPageSize, searchQuery, setSearchQuery, handleSearchClick, setSortBy, setSortDir } = useFetchData();
    const [selectedActive, setSelectedActive] = useState('true');
    const [selectedRecord, setSelectedRecord] = useState(null);
    const { modalState, handleOpenModal, handleCloseModal } = useModal();
    const { statusBadge } = useStatusBadge();

    const formProps = useForm({
        defaultValues: {
            code: '',
            description: '',
            months: '0',
            days: '0',
            active: true
        }
    });

    useEffect(() => {
        const params = {
            pageNumber,
            pageSize,
            sortBy,
            sortDir,
            searchQuery,
        };
        
        // Add active filter if selectedActive is set
        if (selectedActive !== null && selectedActive !== '') {
            params.active = selectedActive === 'true';
        }
        
        fetchData(noticePeriodService.getNoticePeriods, params);
    }, [pageNumber, pageSize, sortBy, sortDir, selectedActive]);

    const columns = [
        {
            key: 'code',
            label: t('staff.noticePeriod.table.headers.code') || 'Code',
            sortable: true,
        },
        {
            key: 'description',
            label: t('staff.noticePeriod.table.headers.description') || 'Description',
            sortable: true,
        },
        {
            key: 'months',
            label: t('staff.noticePeriod.table.headers.months') || 'Months',
            sortable: true,
        },
        {
            key: 'days',
            label: t('staff.noticePeriod.table.headers.days') || 'Days',
            sortable: true,
        },
        {
            key: 'active',
            label: t('staff.noticePeriod.table.headers.active') || 'Active',
            sortable: true,
        },
        {
            key: 'translated',
            label: t('staff.noticePeriod.table.headers.translated') || 'Translation',
            sortable: true,
        },
    ]

    const actionButtons = (
        <ActionButtons
            buttons={[{
                ...COMMON_BUTTONS.ADD,
                onClick: () => handleOpenModal('add')
            },
            {
                ...COMMON_BUTTONS.EDIT,
                onClick: () => handleOpenModal('edit')
            },
            {
                ...COMMON_BUTTONS.DELETE,
                onClick: () => handleOpenModal('delete')
            },
            {
                ...COMMON_BUTTONS.TRANSLATE,
                onClick: () => handleOpenModal('translate')
            }]}
        selectedRecord={selectedRecord}
        />
    )
    const searchOptions = [{ key: 'description', label: "description" }]

    const handleSelectRecord = (record) => {
        setSelectedRecord(record);
    };

    const handleSort = (column) => {
        let newSortDir;
        if (sortBy === column) {
            newSortDir = sortDir === 'asc' ? 'desc' : 'asc';
        } else {
            newSortDir = 'asc';
            setSortBy(column);
        }
        setSortDir(newSortDir);
        setpageNumber(0);
    };

    const handleFormSubmit = async (formData) => {
        try {
            if (modalState.type === 'edit' && selectedRecord) {
                await noticePeriodService.updateNoticePeriod(selectedRecord.id, formData);
                notifySuccess(t('common.updatedSuccessfully'));
            } else {
                await noticePeriodService.createNoticePeriod(formData);
                notifySuccess(t('common.createdSuccessfully'));
            }
            handleCloseModal();
            const params = {
                pageNumber,
                pageSize,
                sortBy,
                sortDir,
                searchQuery,
            };
            if (selectedActive !== null && selectedActive !== '') {
                params.active = selectedActive === 'true';
            }
            fetchData(noticePeriodService.getNoticePeriods, params);
        } catch (error) {
            console.error('Error saving record:', error);
            const errorMessage = error?.response?.data?.message || error?.response?.data || t('common.errorMessage');
            notifyError(errorMessage);
        }
    };

    const handleConfirmDelete = useDeleteHandler({
        service: noticePeriodService,
        entityName: 'NoticePeriod',
        selectedData: selectedRecord,
        fetchData,
        setSelectedData: setSelectedRecord,
        handleCloseModal,
        customFetchParams: {
            pageNumber,
            pageSize,
            sortBy,
            sortDir,
            searchQuery,
            ...(selectedActive !== null && selectedActive !== '' && { active: selectedActive === 'true' }),
        },
        successMessage: t('common.deletedSuccessfully'),
        errorMessage: t('common.errorMessage'),
    });

    return (
        <>
            <Table
                title={t('staff.noticePeriod.title') || 'Notice Period'}
                columns={columns}
                actionButtons={actionButtons}
                data={data}
                selectedData={selectedRecord?.id}
                handleSearchClick={() => {
                    const params = { searchQuery };
                    if (selectedActive !== null && selectedActive !== '') {
                        params.active = selectedActive === 'true';
                    }
                    handleSearchClick(noticePeriodService.getNoticePeriods, params);
                }}
                getData={() => {
                    const params = { searchQuery };
                    if (selectedActive !== null && selectedActive !== '') {
                        params.active = selectedActive === 'true';
                    }
                    fetchData(noticePeriodService.getNoticePeriods, params);
                }}
                handleCheckboxChange={(index) => handleSelectRecord(data[index])}
                pageNumber={pageNumber}
                showPageSizeSelector={false}
                showPagination={true}
                totalPages={totalPages}
                totalElements={totalElements}
                pageSize={pageSize}
                setpageNumber={setpageNumber}
                setPageSize={setPageSize}
                searchOptions={searchOptions}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                showActiveSelect={true}
                selectedActive={selectedActive}
                setSelectedActive={setSelectedActive}
                renderCell={(item, column) => {
                    // If column has a custom render function, use it
                    if (column.render) {
                        return column.render(item);
                    }
                    // Otherwise, use statusBadge for active/translated columns
                    return statusBadge(item, column);
                }}
                sortBy={sortBy}
                sortDir={sortDir}
                handleSort={handleSort}
            />

            {modalState.visible && (modalState.type === 'add' || modalState.type === 'edit') && (
                <NoticePeriodForm
                    onClose={handleCloseModal}
                    onConfirm={handleFormSubmit}
                    editData={modalState.type === 'edit' ? selectedRecord : null}
                    formProps={formProps}
                />
            )}

            {modalState.visible && modalState.type === 'delete' && (
                <DeleteModal
                    onClose={handleCloseModal}
                    onConfirm={handleConfirmDelete}
                    name={selectedRecord?.description || selectedRecord?.code}
                    entityType="record"
                />
            )}

            {modalState.visible && modalState.type === 'translate' && (
                <NoticePeriodTranslation
                    onClose={handleCloseModal}
                    record={selectedRecord}
                    onRefresh={() => {
                        const params = {
                            pageNumber,
                            pageSize,
                            sortBy,
                            sortDir,
                            searchQuery,
                        };
                        if (selectedActive !== null && selectedActive !== '') {
                            params.active = selectedActive === 'true';
                        }
                        return fetchData(noticePeriodService.getNoticePeriods, params);
                    }}
                />
            )}
        </>
    )
}

